import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  DataTakerStaffDataModel,
  PatientBehaviorAntecedentModel,
  PatientBehaviorConsequenceModel,
  PatientBehaviorDataModel,
  PatientBehaviorLocationModel,
  PatientBehaviorModel,
  PatientBehaviorResponseModel,
  PatientBehaviorSeverityModel,
  PatientModel,
  PatientSessionModel,
  PatientTargetDataModel,
  PatientTargetModel,
  Permissions,
  ServiceSparkModel,
  SessionProgressNoteModel,
  UserModel,
} from '../../core/api/models';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../auth';
import {
  HealthCheckApiService,
  SupervisorApiService,
  StaffApiService,
} from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { SupervisorHelperService } from '../../supervisor/supervisor.helper.service';
import { PatientSessionService } from '../home/patient-session.service';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { Utils } from '../../core/utils';
import { SessionsBaseComponent } from './sessions-base.component';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { OfflineDataService } from '../../core/services/offline-data.service';
import { SendLogsService } from '../../core/services/send-logs.service';
import { Table } from 'primeng/table';
import {
  NgSignaturePadOptions,
  SignaturePadComponent,
} from '../../shared/components/signature-pad/signature-pad.component';
import { dataURItoBlob } from '../../shared/utils/utils';
import { FormControl } from '@angular/forms';
import {
  AcademyModeService,
  SparkModeQueryParams,
} from '../../core/services/academy-mode.service';
import { GroupSessionService } from '../../core/services/group-session.service';
import {
  canLinkSessionToService,
  getLinkablePatientServices,
  getPossibleServiceForSession,
  getSessionLinkDate,
  isCurrentStaffOnService,
  isSameLocalDay,
  SAME_DAY_SERVICE_LINK_ERROR,
} from './session-service-link.util';

@Component({
  selector: 'app-sessions',
  templateUrl: './sessions.component.html',
  styleUrl: './sessions.component.css',
  standalone: false,
})
export class SessionsComponent extends SessionsBaseComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table!: Table;
  private readonly componentDestroyRef = inject(DestroyRef);
  private currentPatientID: number | null = null;

  userInfo: UserModel | null = null;

  openMenu: boolean = false;
  showGraph = signal(false);

  showAddData = signal(false);

  selectedSession = signal<PatientSessionModel | null>(null);

  showDeletedSessions = signal(false);
  sessionScope = signal<'all' | 'mine'>('mine');

  // Client info dialog state
  showClientInfo = signal(false);
  clientInfoSession = signal<PatientSessionModel | null>(null);
  showCreateGroupSession = signal(false);
  showCreateSession = signal(false);
  createSessionServiceID: number | null = null;
  createGroupSessionServiceID: number | null = null;
  isCreatingSession = false;
  selectedGroupPatientIDs: number[] = [];
  groupPatientOptions: {
    label: string;
    value: number;
    searchString: string;
  }[] = [];
  isCreatingGroupSession = false;

  clonedSessions: { [s: number]: PatientSessionModel } = {};
  clonedSessionData: {
    [s: number]: PatientTargetDataModel | PatientBehaviorDataModel;
  } = {};

  linkedToServiceOnSessionEditBegin: boolean = false;

  loggedUserID: number | null = null;

  // filters logic
  possibleFilters = computed<SelectItemGroup[]>(() => {
    let targets = this.targets();
    let behaviors = this.behaviors();
    return [
      {
        label: 'Targets',
        items: targets.map((target) => {
          return {
            label: target.TargetName ?? 'No Name for Target',
            value: target,
          };
        }),
      },
      {
        label: 'Behaviors',
        items: behaviors.map((behavior) => {
          return {
            label: behavior.PatientBehaviorName ?? 'No Name for Behavior',
            value: behavior,
          };
        }),
      },
    ];
  });

  selectedFilters = signal<any[]>([]);

  isLoadingSession: { [s: number]: boolean } = {};

  filteredSessions = computed(() => {
    let filteredSessions = this.sessions();
    if (this.sessionScope() === 'mine') {
      filteredSessions = filteredSessions.filter((session) => session.IsCurrentUserParticipant);
    }
    if (this.selectedFilters() == null) {
      return filteredSessions;
    }
    this.selectedFilters().forEach((selectedFilter) => {
      if (this.supervisorHelperService.isPatientTargetModel(selectedFilter)) {
        filteredSessions = filteredSessions.filter((session) =>
          session.TargetData?.some(
            (target) => target.PatientTargetID === selectedFilter.ID
          )
        );
      }
      if (this.supervisorHelperService.isPatientBehaviorModel(selectedFilter)) {
        filteredSessions = filteredSessions.filter((session) =>
          session.BehaviorData?.some(
            (behavior) => behavior.PatientBehaviorID === selectedFilter.ID
          )
        );
      }
    });

    if (!this.showDeletedSessions()) {
      filteredSessions = filteredSessions.filter(
        (session) => !session.DeletedDate
      );
    }

    const seenSessionIds = new Set<number>();
    filteredSessions = filteredSessions.filter((session) => {
      if (session.ID == null) {
        return true;
      }
      if (seenSessionIds.has(session.ID)) {
        return false;
      }
      seenSessionIds.add(session.ID);
      return true;
    });

    return [...filteredSessions].sort((a, b) => {
      if (!a.StartedDate) {
        return -1;
      }
      if (!b.StartedDate) {
        return 1;
      }
      return (
        new Date(b.StartedDate ?? '').getTime() -
        new Date(a.StartedDate ?? '').getTime()
      );
    });
  });

  services: { [s: number]: ServiceSparkModel } = {};
  serviceOptions: SelectItem[] = [];
  linkableServices: ServiceSparkModel[] = [];

  showProgressNotes = signal(false);
  sessionToShowProgressNote = signal<PatientSessionModel | null>(null);

  showHandoffInfo = signal(false);
  handoffInfoSession = signal<PatientSessionModel | null>(null);
  handoffGuardianName = signal<string | null>(null);
  handoffSignatureUrl = signal<string | null>(null);
  handoffStaffSignatureUrl = signal<string | null>(null);
  isLoadingGuardianName = signal(false);
  isLoadingSignature = signal(false);
  isLoadingStaffSignature = signal(false);
  isEditMode = signal(false);

  // Edit mode form controls
  editHandoffType = signal<'staff' | 'parent' | 'temporary' | undefined>(undefined);
  editExplanation = signal<string>('');
  editGuardian = signal<any>(null);
  editOtherGuardianName = signal<string>('');
  editGuardians = signal<any[]>([]);
  editTemporaryStaff = signal<{ StaffID: number; FullName: string } | null>(null);
  editTemporaryStaffOptions = signal<{ StaffID: number; FullName: string }[]>([]);

  showTemporaryHandoffEnd = signal(false);
  temporaryHandoffSession = signal<PatientSessionModel | null>(null);
  temporaryGuardians = signal<any[]>([]);
  temporaryGuardian = signal<any>(null);
  temporaryOtherGuardianName = signal<string>('');
  showEditTemporarySignature = signal(false);

  @ViewChild(SignaturePadComponent, { static: false })
  signaturePad!: SignaturePadComponent;
  @ViewChild('temporarySignatureEditPad', { static: false })
  temporarySignatureEditPad?: SignaturePadComponent;

  public signaturePadOptions: NgSignaturePadOptions = {
    canvasHeight: 300,
    canvasWidth: 1250,
  };

  constructor(
    private authService: AuthService,
    private router: Router,
    protected override supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    private activatedRoute: ActivatedRoute,
    protected override patientSessionService: PatientSessionService,
    protected override staffApiService: StaffApiService,
    private offlineDataService: OfflineDataService,
    private sendLogsService: SendLogsService,
    private http: HttpClient,
    private academyModeService: AcademyModeService,
    private groupSessionService: GroupSessionService
  ) {
    super(
      supervisorApiService,
      supervisorHelperService,
      patientSessionService,
      staffApiService
    );
    console.log(
      'SupervisorMain PatientSessionService instance:',
      this.patientSessionService
    );
  }

  ngOnInit() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe((params) => {
        const patientID = Number(params['patientID']);
        if (!Number.isFinite(patientID) || patientID <= 0) {
          return;
        }
        this.currentPatientID = patientID;
        this.reloadDataForPatient(patientID);
      });

    this.authService
      .getUserInfo()
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe((user) => {
        this.loggedUserID = user?.ID ?? null;
        this.sessionScope.set(user?.Permissions?.includes(Permissions.SparkApp_Supervisor) ? 'all' : 'mine');
        this.refreshLinkableServices();
      });

    this.authService
      .getMyPatients()
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe((patients) => {
        if (!patients) {
          this.groupPatientOptions = [];
          return;
        }
        this.groupPatientOptions = patients
          .filter((patient) => !!patient.ID)
          .map((patient) => this.toGroupPatientOption(patient));
      });

    this.offlineDataService
      .getOfflinePatients()
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe((patients) => {
        patients?.forEach((data, patientID) => {
          if (
            this.groupPatientOptions.some(
              (option) => option.value === patientID
            )
          ) {
            return;
          }
          this.groupPatientOptions.push(
            this.toGroupPatientOption({ ...data.patient, ID: patientID })
          );
        });
        this.groupPatientOptions = [...this.groupPatientOptions];
      });
  }

  ngOnDestroy(): void {
    this.revokeHandoffObjectUrls();
  }

  loadData() {
    if (!this.currentPatientID) {
      return;
    }
    this.reloadDataForPatient(this.currentPatientID);
  }

  private reloadDataForPatient(patientID: number) {
    this.isLoading = true;
    // Capture which rows are expanded before reload, so we can re-fetch their full data afterward
    const expandedSessionIds =
      this.table && this.filteredSessions().length > 0
        ? this.filteredSessions()
            .filter((s) => this.table.isRowExpanded(s))
            .map((s) => s.ID)
            .filter((id): id is number => id != null)
        : [];

    this.loadBaseData(patientID, () => {
      this.isLoading = false;
      this.reloadExpandedSessionsData(expandedSessionIds);
    });

    this.staffApiService
      .GetPatientServices({
        patientID,
      })
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe((services) => {
        this.services = {};
        services.forEach((service) => {
          if (service.ID) {
            this.services[service.ID] = service;
          }
        });

        this.serviceOptions = services.map((service) => ({
          label: 'Service #' + service.ID,
          value: service.ID,
        }));
        this.refreshLinkableServices();
      });
  }

  get canPlotGraph() {
    return true;
  }

  onRowExpand(event: TableRowExpandEvent) {
    console.log('Row expanded:', event);
    //Load the session data
    this.isLoadingSession[event.data.ID] = true;
    this.staffApiService
      .GetPatientSessionWithData({
        sessionID: event.data.ID,
      })
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe((session) => {
        //replace session with the new session
        const sessionIndex = this.sessions().findIndex(
          (s) => s.ID === event.data.ID
        );
        if (sessionIndex !== -1) {
          this.sessions.update((sessions) => {
            // Create a new array to ensure signal detects the change
            const newSessions = [...sessions];
            newSessions[sessionIndex] = this.preserveGroupSessionSummary(
              newSessions[sessionIndex],
              session
            );
            return newSessions;
          });
        }
        this.isLoadingSession[event.data.ID] = false;
      });
  }

  onRowCollapse(event: TableRowCollapseEvent) {
    console.log('Row collapsed:', event);
  }

  /**
   * After a full data reload, re-fetch session-with-data for any rows that were expanded
   * so that the expanded row content (targets/behaviors) continues to display correctly.
   */
  private reloadExpandedSessionsData(expandedSessionIds: number[]) {
    if (expandedSessionIds.length === 0) return;
    forkJoin(
      expandedSessionIds.map((id) =>
        this.staffApiService.GetPatientSessionWithData({ sessionID: id })
      )
    )
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe((fullSessions) => {
      this.sessions.update((sessionsArr) => {
        const next = [...sessionsArr];
        fullSessions.forEach((session) => {
          const i = next.findIndex((s) => s.ID === session.ID);
          if (i !== -1) {
            next[i] = this.preserveGroupSessionSummary(next[i], session);
          }
        });
        return next;
      });
    });
  }

  private preserveGroupSessionSummary(
    summary: PatientSessionModel,
    details: PatientSessionModel
  ): PatientSessionModel {
    return {
      ...details,
      IsCurrentUserParticipant: summary.IsCurrentUserParticipant,
      GroupSessionID: details.GroupSessionID ?? summary.GroupSessionID,
      GroupSessionMembers:
        details.GroupSessionMembers?.length
          ? details.GroupSessionMembers
          : summary.GroupSessionMembers,
    };
  }

  switchToStaff() {
    const patientSession = this.patientSessionService.getPatientSession();
    const patientID = patientSession?.PatientID ?? this.selectedPatient?.ID;
    if (!patientID || !patientSession?.ID) {
      return;
    }

    this.router.navigate(['/staff/home', patientID, patientSession.ID], {
      queryParams: this.getAcademyModeQueryParams(),
    });
  }

  backToOpto() {
    window.location.href = this.authService.getOptoURL() ?? '/';
  }

  onSessionEditInit(session: PatientSessionModel) {
    console.log('Editing session:', session);

    this.linkedToServiceOnSessionEditBegin = session.ServiceID != null;

    this.expandRowById(session.ID ?? 0);

    this.clonedSessions[session.ID as number] = {
      ...session,
    } as PatientSessionModel;
  }

  onSessionEditSave(session: PatientSessionModel) {
    console.log('Updated Session:', session);
    this.saveSessionData(session);
  }

  onSessionEditCancel(session: PatientSessionModel) {
    console.log('Cancelled editing session:', session);
    const sessionId = session.ID as number;
    const cloned = this.clonedSessions[sessionId];
    if (!cloned) {
      return;
    }

    this.sessions.update((sessions) => {
      const index = sessions.findIndex((item) => item.ID === sessionId);
      if (index === -1) {
        return sessions;
      }
      const newSessions = [...sessions];
      newSessions[index] = cloned;
      return newSessions;
    });
    delete this.clonedSessions[sessionId];
  }

  saveSessionData(newSessionData: PatientSessionModel) {
    this.isLoading = true;
    console.log('Saving session data:', newSessionData);

    if (
      newSessionData.ServiceID &&
      !canLinkSessionToService(
        newSessionData,
        this.services[newSessionData.ServiceID]
      )
    ) {
      this.alertService.error(SAME_DAY_SERVICE_LINK_ERROR);
      this.isLoading = false;
      return;
    }

    // check for other sessions with same ServiceID and show alert if there are more than 1
    const otherSession = this.sessions().find(
      (s) =>
            s.ServiceID != null &&
            s.ServiceID === newSessionData.ServiceID &&
        s.ID !== newSessionData.ID &&
        !s.DeletedDate
    );
    if (otherSession) {
      const errorMessage =
        'There is already another session(#' +
        otherSession.ID +
        ') from ' +
        this.formattedDatesForSession(
          otherSession.StartedDate,
          otherSession.FinishedDate
        ) +
        ' with the same ServiceID(#' +
        newSessionData.ServiceID +
        ') you are trying to save. Please confirm if you wish to leave that other session(#' +
        otherSession.ID +
        ') without service and save service(#' +
        newSessionData.ServiceID +
        ') to session(#' +
        newSessionData.ID +
        ') instead.';
      this.alertService
        .confirm(errorMessage, 'Save Service to Different Session')
        .subscribe((response: boolean) => {
          if (response) {
            this.saveSessionDataCall(newSessionData, true);
          } else {
            this.isLoading = false;
          }
        });
      return;
    } else {
      this.saveSessionDataCall(newSessionData, false);
    }
  }

  saveSessionDataCall(
    newSessionData: PatientSessionModel,
    shouldChangeSession: boolean = false
  ) {
    // Update UserID for target/behavior data
    // ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate
    // are all set by the backend and should not be set here
    const currentUser = this.authService.userInfo;
    const currentUserID = currentUser?.ID;

    if (newSessionData) {
      // Update TargetData
      if (newSessionData.TargetData) {
        newSessionData.TargetData.forEach((targetData) => {
          // Preserve original UserID if editing existing record, otherwise ensure UserID is set
          if (!targetData.UserID && currentUserID) {
            // New record without UserID: use current user (should be set from session staff)
            targetData.UserID = currentUserID;
            targetData.UserFullName = currentUser?.Fullnane ?? '';
          }
          // If editing existing record, UserID should already be set (original creator)
        });
      }

      // Update BehaviorData
      if (newSessionData.BehaviorData) {
        newSessionData.BehaviorData.forEach((behaviorData) => {
          // Preserve original UserID if editing existing record, otherwise ensure UserID is set
          if (!behaviorData.UserID && currentUserID) {
            // New record without UserID: use current user (should be set from session staff)
            behaviorData.UserID = currentUserID;
            behaviorData.UserFullName = currentUser?.Fullnane ?? '';
          }
          // If editing existing record, UserID should already be set (original creator)
        });
      }
    }

    this.staffApiService
      .SavePatientSession({
        body: newSessionData,
        confirmSessionSwitch: shouldChangeSession,
      })
      .subscribe(
        (response) => {
          console.log('Saved session data:', response);
          this.alertService.success('Session data saved successfully');
          this.isLoading = false;
          this.loadData();
        },
        (error) => {
          console.error('Error saving session data:', error);
          this.alertService.error(
            'Error saving session data - ' + error.Message
          );
          this.isLoading = false;
        }
      );
  }

  handOffTextForSession(session: PatientSessionModel) {
    if (this.isPendingTemporaryHandoff(session)) {
      return 'Temporary Handoff';
    } else if (session.IsHandoffToStaff) {
      return 'Handoff to Staff';
    } else if (session.IsHandoffToParent) {
      return 'Handoff to Parent';
    } else {
      return 'Not set';
    }
  }

  severityForHandoffTypeOfSession(session: PatientSessionModel) {
    if (this.isPendingTemporaryHandoff(session)) {
      return 'contrast';
    } else if (session.IsHandoffToStaff) {
      return 'success';
    } else if (session.IsHandoffToParent) {
      return 'warning';
    } else {
      return 'info';
    }
  }

  shouldShowHandoffViewButtonForSession(session: PatientSessionModel) {
    if (session.FinishedDate == null || session.FinishedDate === '') {
      return false;
    }

    if (this.isPendingTemporaryHandoff(session)) {
      return this.canCurrentUserEndTemporaryHandoff(session);
    }

    return true;
  }

  onHandoffBadgeClick(session: PatientSessionModel) {
    if (this.isPendingTemporaryHandoff(session)) {
      if (!this.canCurrentUserEndTemporaryHandoff(session)) {
        this.alertService.error(
          'Only the selected temporary handoff staff can finish this handoff.'
        );
        return;
      }
      this.openTemporaryHandoffEndDialog(session);
      return;
    }

    this.viewHandoffInfo(session);
  }

  temporaryHandoffTooltip(session: PatientSessionModel): string {
    if (!this.isPendingTemporaryHandoff(session)) {
      return '';
    }

    if (!session.TemporaryHandoffStaffName) {
      return 'Only the selected temporary handoff staff can complete this handoff.';
    }

    return `Temporary handoff assigned to ${session.TemporaryHandoffStaffName}`;
  }

  private isPendingTemporaryHandoff(session: PatientSessionModel): boolean {
    return !!(
      session.IsTemporaryHandoff &&
      session.TemporaryHandoffStaffID &&
      !session.TemporaryHandoffEndedDate &&
      session.FinishedDate
    );
  }

  private canCurrentUserEndTemporaryHandoff(session: PatientSessionModel): boolean {
    const currentUserId = this.loggedUserID ?? this.authService.userInfo?.ID ?? null;
    if (!session.TemporaryHandoffStaffID || !currentUserId) {
      return false;
    }

    return session.TemporaryHandoffStaffID === currentUserId;
  }

  viewHandoffInfo(session: PatientSessionModel) {
    this.handoffInfoSession.set(session);
    this.showHandoffInfo.update((oldValue) => true);
    this.isEditMode.set(false);

    // Initialize edit mode values
    if (session.IsTemporaryHandoff) {
      this.editHandoffType.set('temporary');
    } else if (session.IsHandoffToStaff) {
      this.editHandoffType.set('staff');
    } else if (session.IsHandoffToParent) {
      this.editHandoffType.set('parent');
    } else {
      this.editHandoffType.set(undefined);
    }
    this.editExplanation.set(session.Explanation ?? '');
    this.editOtherGuardianName.set(session.OtherGuardianName ?? '');
    this.loadTemporaryStaffOptionsForEdit(session);

    // Load guardian name if it's a parent handoff
    if (session.IsHandoffToParent && session.PatientID) {
      this.loadGuardianName(session);
      this.loadGuardiansForEdit(session);
      // Load signature if available
      if (session.ID) {
        this.loadGuardianSignature(session.ID);
        if (session.TemporaryHandoffStaffID || session.StaffHandoffSignatureAwsKey) {
          this.loadStaffHandoffSignature(session.ID);
        } else {
          this.handoffStaffSignatureUrl.set(null);
          this.isLoadingStaffSignature.set(false);
        }
      }
    } else {
      this.handoffGuardianName.set(null);
      this.handoffSignatureUrl.set(null);
      if (session.ID && (session.IsHandoffToStaff || session.IsTemporaryHandoff)) {
        this.loadStaffHandoffSignature(session.ID);
      } else {
        this.handoffStaffSignatureUrl.set(null);
        this.isLoadingStaffSignature.set(false);
      }
      if (session.PatientID) {
        this.loadGuardiansForEdit(session);
      }
    }
  }

  loadGuardiansForEdit(session: PatientSessionModel) {
    if (!session.PatientID) {
      return;
    }

    this.staffApiService
      .GetPatientGuardians({ patientID: session.PatientID })
      .subscribe({
        next: (guardians) => {
          const guardiansList = [...guardians];
          guardiansList.push({ Name: 'Other', EFI_LiablePersonID: undefined });
          this.editGuardians.set(guardiansList);

          // Set current guardian if editing
          if (session.OtherGuardianName) {
            this.editGuardian.set({
              Name: 'Other',
              EFI_LiablePersonID: undefined,
            });
          } else if (session.EFI_LiablePersonID) {
            const guardian = guardiansList.find(
              (g) => g.EFI_LiablePersonID === session.EFI_LiablePersonID
            );
            this.editGuardian.set(guardian ?? undefined);
          }
        },
        error: () => {
          this.editGuardians.set([
            { Name: 'Other', EFI_LiablePersonID: undefined },
          ]);
        },
      });
  }

  closeHandoffInfo() {
    // Clean up object URL if it was created
    this.revokeHandoffObjectUrls();
    this.showHandoffInfo.update((oldValue) => false);
    this.handoffInfoSession.set(null);
    this.handoffGuardianName.set(null);
    this.handoffSignatureUrl.set(null);
    this.handoffStaffSignatureUrl.set(null);
    this.editTemporaryStaff.set(null);
    this.editTemporaryStaffOptions.set([]);
    this.isLoadingGuardianName.set(false);
    this.isLoadingSignature.set(false);
    this.isLoadingStaffSignature.set(false);
    this.showEditTemporarySignature.set(false);
  }

  private revokeHandoffObjectUrls(): void {
    const signatureUrl = this.handoffSignatureUrl();
    if (signatureUrl && signatureUrl.startsWith('blob:')) {
      URL.revokeObjectURL(signatureUrl);
    }
    const staffSignatureUrl = this.handoffStaffSignatureUrl();
    if (staffSignatureUrl && staffSignatureUrl.startsWith('blob:')) {
      URL.revokeObjectURL(staffSignatureUrl);
    }
  }

  private hasTemporaryHandoffInfo(session: PatientSessionModel | null | undefined): boolean {
    return !!(
      session?.IsTemporaryHandoff ||
      session?.TemporaryHandoffStaffID ||
      session?.TemporaryHandoffStaffName ||
      session?.TemporaryHandoffStartedDate ||
      session?.TemporaryHandoffEndedDate
    );
  }

  private loadTemporaryStaffOptionsForEdit(session: PatientSessionModel) {
    if (!this.hasTemporaryHandoffInfo(session)) {
      this.editTemporaryStaffOptions.set([]);
      this.editTemporaryStaff.set(null);
      return;
    }

    this.staffApiService.GetActiveStaffMembers().subscribe({
      next: (staffList: DataTakerStaffDataModel[]) => {
        const options = (staffList ?? [])
          .filter((staff) => !!staff.StaffID)
          .map((staff) => ({
            StaffID: staff.StaffID as number,
            FullName: (staff.FullName ?? '').trim() || `Staff #${staff.StaffID}`,
          }))
          .sort((a, b) => a.FullName.localeCompare(b.FullName));

        this.editTemporaryStaffOptions.set(options);

        if (session.TemporaryHandoffStaffID) {
          const selected = options.find(
            (staff) => staff.StaffID === session.TemporaryHandoffStaffID
          );
          if (selected) {
            this.editTemporaryStaff.set(selected);
            return;
          }
        }

        if (session.TemporaryHandoffStaffName && session.TemporaryHandoffStaffID) {
          this.editTemporaryStaff.set({
            StaffID: session.TemporaryHandoffStaffID,
            FullName: session.TemporaryHandoffStaffName,
          });
        } else {
          this.editTemporaryStaff.set(options[0] ?? null);
        }
      },
      error: () => {
        this.editTemporaryStaffOptions.set([]);
        if (session.TemporaryHandoffStaffID && session.TemporaryHandoffStaffName) {
          this.editTemporaryStaff.set({
            StaffID: session.TemporaryHandoffStaffID,
            FullName: session.TemporaryHandoffStaffName,
          });
        } else {
          this.editTemporaryStaff.set(null);
        }
      },
    });
  }

  loadGuardianName(session: PatientSessionModel) {
    if (!session.PatientID) {
      this.handoffGuardianName.set(null);
      this.isLoadingGuardianName.set(false);
      return;
    }

    this.isLoadingGuardianName.set(true);
    this.staffApiService
      .GetPatientGuardians({ patientID: session.PatientID })
      .subscribe({
        next: (guardians) => {
          if (session.OtherGuardianName) {
            // Use OtherGuardianName if set
            this.handoffGuardianName.set(session.OtherGuardianName);
          } else if (session.EFI_LiablePersonID) {
            // Find guardian by EFI_LiablePersonID
            const guardian = guardians.find(
              (g) => g.EFI_LiablePersonID === session.EFI_LiablePersonID
            );
            this.handoffGuardianName.set(guardian?.Name ?? 'Unknown Guardian');
          } else {
            this.handoffGuardianName.set(null);
          }
          this.isLoadingGuardianName.set(false);
        },
        error: () => {
          this.handoffGuardianName.set(null);
          this.isLoadingGuardianName.set(false);
        },
      });
  }

  loadGuardianSignature(sessionID: number) {
    this.isLoadingSignature.set(true);

    // First, try to get signature from offline storage (in case it was just saved)
    this.offlineDataService
      .getSessionGuardianSignature(sessionID)
      .then((offlineSignature: Blob | null) => {
        if (offlineSignature && offlineSignature.size > 0) {
          // Found in offline storage, use it (this is the most recent, unsynced version)
          const url = URL.createObjectURL(offlineSignature);
          this.handoffSignatureUrl.set(url);
          this.isLoadingSignature.set(false);
          // Don't check server - offline version takes priority since it hasn't been synced yet
        } else {
          // Not in offline storage, try server
          this.loadGuardianSignatureFromServer(sessionID);
        }
      })
      .catch(() => {
        // Error getting from offline storage, try server
        this.loadGuardianSignatureFromServer(sessionID);
      });
  }

  private loadGuardianSignatureFromServer(sessionID: number) {
    const apiPath = StaffApiService.ApiStaffDownloadGuardianSignatureGetPath;
    const url = `${this.staffApiService.rootUrl}${apiPath}?sessionID=${sessionID}`;

    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        if (blob && blob.size > 0) {
          // Only use server signature if we don't already have one from offline storage
          if (!this.handoffSignatureUrl()) {
            // Clean up old URL if it exists
            const oldUrl = this.handoffSignatureUrl();
            if (oldUrl && oldUrl.startsWith('blob:')) {
              URL.revokeObjectURL(oldUrl);
            }
            // Create object URL from blob
            const newUrl = URL.createObjectURL(blob);
            this.handoffSignatureUrl.set(newUrl);
          }
        } else {
          // Only set to null if we don't already have a signature from offline storage
          if (!this.handoffSignatureUrl()) {
            this.handoffSignatureUrl.set(null);
          }
        }
        this.isLoadingSignature.set(false);
      },
      error: (error) => {
        console.error('Error loading guardian signature from server:', error);
        // Only set to null if we don't already have a signature from offline storage
        if (!this.handoffSignatureUrl()) {
          this.handoffSignatureUrl.set(null);
        }
        this.isLoadingSignature.set(false);
      },
    });
  }

  loadStaffHandoffSignature(sessionID: number) {
    this.isLoadingStaffSignature.set(true);

    this.offlineDataService
      .getSessionStaffHandoffSignature(sessionID)
      .then((offlineSignature: Blob | null) => {
        if (offlineSignature && offlineSignature.size > 0) {
          const url = URL.createObjectURL(offlineSignature);
          this.handoffStaffSignatureUrl.set(url);
          this.isLoadingStaffSignature.set(false);
        } else {
          this.loadStaffHandoffSignatureFromServer(sessionID);
        }
      })
      .catch(() => {
        this.loadStaffHandoffSignatureFromServer(sessionID);
      });
  }

  private loadStaffHandoffSignatureFromServer(sessionID: number) {
    const apiPath = StaffApiService.ApiStaffDownloadStaffHandoffSignatureGetPath;
    const url = `${this.staffApiService.rootUrl}${apiPath}?sessionID=${sessionID}`;

    this.http
      .get(url, { responseType: 'blob' })
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe({
        next: (blob) => {
          if (blob && blob.size > 0) {
            if (!this.handoffStaffSignatureUrl()) {
          const oldUrl = this.handoffStaffSignatureUrl();
          if (oldUrl && oldUrl.startsWith('blob:')) {
            URL.revokeObjectURL(oldUrl);
          }
            this.handoffStaffSignatureUrl.set(URL.createObjectURL(blob));
            }
          } else if (!this.handoffStaffSignatureUrl()) {
            this.handoffStaffSignatureUrl.set(null);
          }
          this.isLoadingStaffSignature.set(false);
        },
        error: () => {
          if (!this.handoffStaffSignatureUrl()) {
          this.handoffStaffSignatureUrl.set(null);
          }
          this.isLoadingStaffSignature.set(false);
        },
      });
  }

  formattedSessionName(session: PatientSessionModel | null): string {
    if (!session) {
      return '';
    }

    const formattedDates = this.formattedDatesForSession(
      session.StartedDate,
      session.FinishedDate
    );
    const serviceInfo = session.ServiceID
      ? ` (Service: #${session.ServiceID}${
          session.StaffNames && session.StaffNames.length > 0
            ? ' - ' + session.StaffNames.join(', ')
            : ''
        })`
      : '';

    return `Session ID ${session.ID ?? 'N/A'}${
      serviceInfo ? serviceInfo : ''
    } - ${formattedDates}`;
  }

  formattedDatesForSession(
    startDate: string | null | undefined,
    endDate: string | null | undefined
  ): string {
    if (!startDate) {
      return 'Not started';
    }

    if (!endDate) {
      return new Date(startDate).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    // If on the same day, return the date and 2 times
    if (
      new Date(startDate).toDateString() === new Date(endDate).toDateString()
    ) {
      let result = new Date(startDate).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      // Add end time to the result
      result +=
        ' - ' +
        new Date(endDate).toLocaleString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
      return result;
    }

    // Date in format MM/DD/YYYY hh:mm AM/PM
    return (
      new Date(startDate).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }) +
      ' - ' +
      new Date(endDate).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    );
  }

  editHandoffInfo() {
    this.isEditMode.set(true);
  }

  cancelEdit() {
    this.isEditMode.set(false);
    // Reset to original values
    const session = this.handoffInfoSession();
    if (session) {
      if (session.IsTemporaryHandoff) {
        this.editHandoffType.set('temporary');
      } else if (session.IsHandoffToStaff) {
        this.editHandoffType.set('staff');
      } else if (session.IsHandoffToParent) {
        this.editHandoffType.set('parent');
      } else {
        this.editHandoffType.set(undefined);
      }
      this.editExplanation.set(session.Explanation ?? '');
      this.editOtherGuardianName.set(session.OtherGuardianName ?? '');
      this.loadTemporaryStaffOptionsForEdit(session);
    }
  }

  saveHandoffInfo() {
    const session = this.handoffInfoSession();
    if (!session) {
      return;
    }

    // Check if signature was drawn before saving
    let guardianSignatureWasDrawn = false;
    let staffSignatureWasDrawn = false;
    if (
      (this.editHandoffType() === 'parent' ||
        this.editHandoffType() === 'staff' ||
        this.editHandoffType() === 'temporary') &&
      this.signaturePad
    ) {
      const signatureData = this.signaturePad.toDataURL();
      // Check if signature pad has content (not empty/blank)
      const signatureWasDrawn =
        signatureData &&
        signatureData !== 'data:,' &&
        typeof signatureData === 'string' &&
        signatureData.length > 0
          ? true
          : false;
      if (this.editHandoffType() === 'parent') {
        guardianSignatureWasDrawn = signatureWasDrawn;
      } else if (
        this.editHandoffType() === 'staff' ||
        this.editHandoffType() === 'temporary'
      ) {
        staffSignatureWasDrawn = signatureWasDrawn;
      }
    }

    // Update handoff type
    if (this.editHandoffType() === 'staff') {
      session.IsHandoffToStaff = true;
      session.IsHandoffToParent = false;
      session.IsTemporaryHandoff = false;
      session.TemporaryHandoffStaffID = null;
      session.TemporaryHandoffStaffName = null;
      session.TemporaryHandoffStartedDate = null;
      session.TemporaryHandoffEndedDate = null;

      if (this.signaturePad && staffSignatureWasDrawn && session.ID) {
        const staffSignature = this.signaturePad.toDataURL();
        const staffSignatureBlob = dataURItoBlob(staffSignature);
        if (staffSignatureBlob && staffSignatureBlob.size > 0) {
          this.patientSessionService.setPatientSessionStaffHandoffSignature(
            session.ID,
            staffSignatureBlob
          );
        }
      }
    } else if (this.editHandoffType() === 'temporary') {
      if (!this.editTemporaryStaff()) {
        this.alertService.error('Please select the temporary handoff staff.');
        return;
      }
      session.IsHandoffToStaff = false;
      session.IsHandoffToParent = false;
      session.IsTemporaryHandoff = true;
      session.TemporaryHandoffStaffID = this.editTemporaryStaff()!.StaffID;
      session.TemporaryHandoffStaffName = this.editTemporaryStaff()!.FullName;
      session.TemporaryHandoffStartedDate =
        session.TemporaryHandoffStartedDate ?? new Date().toServerString();

      if (this.signaturePad && staffSignatureWasDrawn && session.ID) {
        const staffSignature = this.signaturePad.toDataURL();
        const staffSignatureBlob = dataURItoBlob(staffSignature);
        if (staffSignatureBlob && staffSignatureBlob.size > 0) {
          this.patientSessionService.setPatientSessionStaffHandoffSignature(
            session.ID,
            staffSignatureBlob
          );
        }
      }
    } else if (this.editHandoffType() === 'parent') {
      session.IsHandoffToStaff = false;
      session.IsHandoffToParent = true;

      if (this.hasTemporaryHandoffInfo(session) && this.editTemporaryStaff()) {
        session.TemporaryHandoffStaffID = this.editTemporaryStaff()!.StaffID;
        session.TemporaryHandoffStaffName = this.editTemporaryStaff()!.FullName;
      }

      // Update guardian info
      if (this.editGuardian()) {
        if (this.editGuardian().Name === 'Other') {
          session.OtherGuardianName = this.editOtherGuardianName();
          session.EFI_LiablePersonID = null;
        } else {
          session.EFI_LiablePersonID = this.editGuardian().EFI_LiablePersonID;
          session.OtherGuardianName = '';
        }

        // Save signature if available
        if (this.signaturePad && guardianSignatureWasDrawn) {
          const guardianSignature = this.signaturePad.toDataURL();
          const guardianSignatureBlob = dataURItoBlob(guardianSignature);
          if (
            session.ID &&
            guardianSignatureBlob &&
            guardianSignatureBlob.size > 0
          ) {
            this.patientSessionService.setPatientSessionGuardianSignature(
              session.ID,
              guardianSignatureBlob
            );
          }
        }
      }
    }

    // Update explanation
    session.Explanation = this.editExplanation();

    // Save session
    this.isLoading = true;
    this.staffApiService.SavePatientSession({ body: session }).subscribe({
      next: () => {
        this.alertService.success('Handoff information updated successfully');
        this.isEditMode.set(false);
        // Reload guardian name display
        if (session.IsHandoffToParent && session.PatientID) {
          this.loadGuardianName(session);
          // Reload signature if it was drawn and saved
          if (session.ID && guardianSignatureWasDrawn) {
            // Clean up old signature URL before reloading
            const oldSignatureUrl = this.handoffSignatureUrl();
            if (oldSignatureUrl && oldSignatureUrl.startsWith('blob:')) {
              URL.revokeObjectURL(oldSignatureUrl);
            }
            this.handoffSignatureUrl.set(null);
            // Reload signature from offline storage (where it was just saved)
            this.loadGuardianSignature(session.ID);
          }
          this.handoffStaffSignatureUrl.set(null);
        } else if (session.IsHandoffToStaff || session.IsTemporaryHandoff) {
          this.handoffGuardianName.set(null);
          this.handoffSignatureUrl.set(null);
          if (session.ID && staffSignatureWasDrawn) {
            const oldStaffSignatureUrl = this.handoffStaffSignatureUrl();
            if (oldStaffSignatureUrl && oldStaffSignatureUrl.startsWith('blob:')) {
              URL.revokeObjectURL(oldStaffSignatureUrl);
            }
            this.handoffStaffSignatureUrl.set(null);
          }
          if (session.ID) {
            this.loadStaffHandoffSignature(session.ID);
          }
        } else {
          this.handoffGuardianName.set(null);
          // Clean up signature URL if switching away from parent handoff
          const oldSignatureUrl = this.handoffSignatureUrl();
          if (oldSignatureUrl && oldSignatureUrl.startsWith('blob:')) {
            URL.revokeObjectURL(oldSignatureUrl);
          }
          this.handoffSignatureUrl.set(null);
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        this.alertService.error(
          'Error updating handoff information: ' +
            (error.Message || 'Unknown error')
        );
        this.isLoading = false;
      },
    });
  }

  openEditTemporarySignatureDialog() {
    this.showEditTemporarySignature.set(true);
  }

  closeEditTemporarySignatureDialog() {
    this.showEditTemporarySignature.set(false);
  }

  saveTemporaryHandoffSignatureEdit() {
    const session = this.handoffInfoSession();
    if (!session?.ID) {
      this.alertService.error('Unable to update temporary handoff signature.');
      return;
    }

    if (!this.temporarySignatureEditPad) {
      this.alertService.error('Please provide the temporary handoff signature.');
      return;
    }

    const signatureData = this.temporarySignatureEditPad.toDataURL();
    const signatureBlob = dataURItoBlob(signatureData);
    if (!signatureBlob || signatureBlob.size <= 0) {
      this.alertService.error('Please provide the temporary handoff signature.');
      return;
    }

    this.patientSessionService.setPatientSessionStaffHandoffSignature(
      session.ID,
      signatureBlob
    );
    this.closeEditTemporarySignatureDialog();

    const oldStaffSignatureUrl = this.handoffStaffSignatureUrl();
    if (oldStaffSignatureUrl && oldStaffSignatureUrl.startsWith('blob:')) {
      URL.revokeObjectURL(oldStaffSignatureUrl);
    }
    this.handoffStaffSignatureUrl.set(null);
    this.loadStaffHandoffSignature(session.ID);
    this.alertService.success('Temporary handoff signature updated successfully');
  }

  openTemporaryHandoffEndDialog(session: PatientSessionModel) {
    this.temporaryHandoffSession.set(session);
    this.temporaryGuardian.set(null);
    this.temporaryOtherGuardianName.set('');
    this.showTemporaryHandoffEnd.set(true);
    this.loadTemporaryGuardians(session);
  }

  closeTemporaryHandoffEndDialog() {
    this.showTemporaryHandoffEnd.set(false);
    this.temporaryHandoffSession.set(null);
    this.temporaryGuardians.set([]);
    this.temporaryGuardian.set(null);
    this.temporaryOtherGuardianName.set('');
  }

  private loadTemporaryGuardians(session: PatientSessionModel) {
    if (!session.PatientID) {
      this.temporaryGuardians.set([
        { Name: 'Other', EFI_LiablePersonID: undefined },
      ]);
      return;
    }

    this.staffApiService.GetPatientGuardians({ patientID: session.PatientID }).subscribe({
      next: (guardians) => {
        const guardiansList = [...guardians];
        guardiansList.push({ Name: 'Other', EFI_LiablePersonID: undefined });
        this.temporaryGuardians.set(guardiansList);
      },
      error: () => {
        this.temporaryGuardians.set([
          { Name: 'Other', EFI_LiablePersonID: undefined },
        ]);
      },
    });
  }

  finalizeTemporaryHandoffToGuardian() {
    const session = this.temporaryHandoffSession();
    if (!session) {
      return;
    }

    const selectedGuardian = this.temporaryGuardian();
    if (!selectedGuardian) {
      this.alertService.error('Please select a guardian.');
      return;
    }

    if (
      selectedGuardian.Name === 'Other' &&
      !this.temporaryOtherGuardianName().trim()
    ) {
      this.alertService.error('Please enter the guardian name.');
      return;
    }

    if (!this.signaturePad) {
      this.alertService.error('Please provide the guardian signature.');
      return;
    }

    const signatureData = this.signaturePad.toDataURL();
    const signatureBlob = dataURItoBlob(signatureData);
    if (!signatureBlob || signatureBlob.size <= 0) {
      this.alertService.error('Please provide the guardian signature.');
      return;
    }

    if (selectedGuardian.Name === 'Other') {
      session.OtherGuardianName = this.temporaryOtherGuardianName().trim();
      session.EFI_LiablePersonID = null;
    } else {
      session.EFI_LiablePersonID = selectedGuardian.EFI_LiablePersonID;
      session.OtherGuardianName = '';
    }

    session.IsHandoffToStaff = false;
    session.IsHandoffToParent = true;
    session.IsTemporaryHandoff = false;
    session.TemporaryHandoffEndedDate = new Date().toServerString();
    session.Explanation = session.Explanation ?? '';

    if (session.ID) {
      this.patientSessionService.setPatientSessionGuardianSignature(
        session.ID,
        signatureBlob
      );
    }

    this.isLoading = true;
    this.staffApiService.SavePatientSession({ body: session }).subscribe({
      next: () => {
        this.alertService.success('Temporary handoff completed successfully');
        this.closeTemporaryHandoffEndDialog();
        this.isLoading = false;
        this.loadData();
      },
      error: (error: any) => {
        this.alertService.error(
          'Error finalizing temporary handoff: ' +
            (error.Message || 'Unknown error')
        );
        this.isLoading = false;
      },
    });
  }

  onHandoffTypeChange() {
    if (
      this.editHandoffType() === 'temporary' &&
      !this.editTemporaryStaff() &&
      this.editTemporaryStaffOptions().length > 0
    ) {
      this.editTemporaryStaff.set(this.editTemporaryStaffOptions()[0]);
    }

  }

  onShowGraphClicked(session: PatientSessionModel) {
    console.log('Show graph for session:', session);
    this.showGraph.update((oldValue) => !oldValue);
  }

  // Toolbar Methods
  hideAddData() {
    this.showAddData.update((oldValue) => false);
  }

  showAddDataAlert(session: PatientSessionModel) {
    this.selectedSession.set(session);
    this.showAddData.update((oldValue) => true);
  }

  showDeleteSessionAlert(session: PatientSessionModel) {
    const reasonForDeleteFail = this.reasonForDeleteFail(session);

    if (reasonForDeleteFail) {
      this.alertService.error(reasonForDeleteFail);
      return;
    }

    const deleteSessionMessage = this.hasData(session)
      ? 'This session has data. Deleting it will also delete ALL of the recorded data for this session and unlink it from any service. Are you sure you want to continue?'
      : 'Are you sure you want to delete this session?';

    this.alertService
      .confirm('Delete Session', deleteSessionMessage)
      .subscribe((response: boolean) => {
        if (response) {
          this.deleteSession(session);
        }
      });
  }

  hasData(session: PatientSessionModel) {
    return (
      (session.TargetData?.length && session.TargetData?.length > 0) ||
      (session.BehaviorData?.length && session.BehaviorData?.length > 0)
    );
  }

  deleteSession(session: PatientSessionModel) {
    console.log('Deleting Session:', session);
    session.ServiceID = null;
    session.DeletedDate = new Date().toServerString();

    this.saveSessionData(session);
  }

  reasonForDeleteFail(session: PatientSessionModel) {
    const isSparkSupervisor =
      this.authService.userInfo?.Permissions?.includes(
        Permissions.SparkApp_Supervisor
      ) ?? false;
    const isAttachedToService = this.linkedToServiceOnSessionEditBegin;

    if (!isSparkSupervisor) {
      return 'You are not authorized to delete this session';
    } else if (isAttachedToService) {
      return 'This session cannot be deleted because it is attached to a service. First save the session without the service then try again.';
    } else {
      return null;
    }
  }

  onCloseAddData(event: boolean) {
    this.showAddData.update((oldValue) => false);
    this.loadData();
  }

  onSessionDataDeleted(event: boolean) {
    this.loadData();
  }

  isLoggedUserOnSession(session: PatientSessionModel) {
    return (
      !session.StaffID ||
      session.StaffID == this.loggedUserID ||
      session.StaffIDs?.includes(this.loggedUserID ?? 0)
    );
  }

  getStaffNamesForSession(session: PatientSessionModel) {
    if (!session.StaffNames || session.StaffNames.length == 0) {
      return session.StaffName;
    }

    const uniqueNames = new Set(session.StaffNames);
    return Array.from(uniqueNames).join(', ');
  }

  createSession() {
    this.createSessionServiceID = null;
    this.refreshLinkableServices();
    this.showCreateSession.set(true);
  }

  closeCreateSession(): void {
    this.showCreateSession.set(false);
    this.createSessionServiceID = null;
    this.isCreatingSession = false;
  }

  onCreateSessionVisibleChange(visible: boolean): void {
    if (visible) {
      this.showCreateSession.set(true);
      return;
    }
    this.closeCreateSession();
  }

  confirmCreateSession(): void {
    const patientID = this.selectedPatient?.ID ?? 0;
    if (!patientID) {
      this.alertService.error('Current client is missing');
      return;
    }

    if (
      this.createSessionServiceID &&
      !canLinkSessionToService(
        { StartedDate: null },
        this.services[this.createSessionServiceID]
      )
    ) {
      this.alertService.error(SAME_DAY_SERVICE_LINK_ERROR);
      return;
    }

    const createdWithoutService = !this.createSessionServiceID;
    this.isCreatingSession = true;
    this.isLoading = true;
    this.staffApiService
      .SavePatientSession({
        body: {
          PatientID: patientID,
          ServiceID: this.createSessionServiceID ?? undefined,
        },
      })
      .subscribe({
        next: (response) => {
          console.log('Created session:', response);
          this.closeCreateSession();
          if (createdWithoutService) {
            this.isLoading = false;
            this.openSession(response);
            return;
          }
          this.loadData();
        },
        error: (error) => {
          console.error('Error creating session:', error);
          this.alertService.error(
            'Error creating session - ' + error.Message
          );
          this.isCreatingSession = false;
          this.isLoading = false;
        },
      });
  }

  private refreshLinkableServices(): void {
    this.linkableServices = getLinkablePatientServices(
      Object.values(this.services),
      this.loggedUserID ?? this.authService.userInfo?.ID ?? null,
      { matchDate: new Date() }
    );
  }

  possibleServiceFor(session: PatientSessionModel): ServiceSparkModel | null {
    const matchDate = getSessionLinkDate(session);
    const candidates = getLinkablePatientServices(
      Object.values(this.services),
      this.loggedUserID ?? this.authService.userInfo?.ID ?? null,
      { matchDate }
    );
    return getPossibleServiceForSession(session, candidates);
  }

  getServiceOptionsForSession(session: PatientSessionModel): SelectItem[] {
    const matchDate = getSessionLinkDate(session);
    const currentUserId =
      this.loggedUserID ?? this.authService.userInfo?.ID ?? null;
    const showAllStaff = this.hasSupervisorPermission();
    return Object.values(this.services)
      .filter((service) => !!service.ID)
      .filter(
        (service) =>
          service.ID === session.ServiceID ||
          isSameLocalDay(service.Date, matchDate)
      )
      .filter(
        (service) =>
          showAllStaff ||
          service.ID === session.ServiceID ||
          isCurrentStaffOnService(service, currentUserId)
      )
      .map((service) => ({
        label: 'Service #' + service.ID,
        value: service.ID,
      }));
  }

  getServiceOptionsEmptyMessage(): string {
    return this.hasSupervisorPermission()
      ? "No services on this session's date."
      : "No services on this session's date assigned to you.";
  }

  editSessionFromHint(session: PatientSessionModel, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isSessionDeleted(session)) {
      return;
    }

    const possible = this.possibleServiceFor(session);
    if (!possible?.ID) {
      return;
    }

    this.onSessionEditInit(session);
    session.ServiceID = possible.ID;
    this.table?.initRowEdit(session);
  }

  get currentGroupPatientID(): number | null {
    return this.currentPatientID ?? this.selectedPatient?.ID ?? null;
  }

  get currentGroupPatientName(): string {
    const currentPatientID = this.currentGroupPatientID;
    if (currentPatientID) {
      const fromOptions = this.groupPatientOptions.find(
        (option) => option.value === currentPatientID
      );
      if (fromOptions?.label) {
        return fromOptions.label;
      }
    }
    if (this.selectedPatient) {
      return `${this.selectedPatient.FirstName ?? ''} ${
        this.selectedPatient.LastName ?? ''
      }`.trim();
    }
    return 'Current client';
  }

  get availableGroupPatientOptions(): {
    label: string;
    value: number;
    searchString: string;
  }[] {
    const currentPatientID = this.currentGroupPatientID;
    const selectedAdditionalIDs = new Set(this.selectedGroupPatientIDs);
    return this.groupPatientOptions.filter(
      (option) =>
        option.value !== currentPatientID &&
        !selectedAdditionalIDs.has(option.value)
    );
  }

  groupPatientLabel(patientID: number): string {
    return (
      this.groupPatientOptions.find((option) => option.value === patientID)
        ?.label ?? String(patientID)
    );
  }

  removeSelectedGroupPatient(patientID: number, event?: Event): void {
    event?.stopPropagation();
    this.selectedGroupPatientIDs = this.selectedGroupPatientIDs.filter(
      (id) => id !== patientID
    );
  }

  private toGroupPatientOption(patient: {
    ID?: number | null;
    FirstName?: string | null;
    LastName?: string | null;
    PreferredName?: string | null;
  }): {
    label: string;
    value: number;
    searchString: string;
  } {
    const firstName = patient.FirstName ?? '';
    const lastName = patient.LastName ?? '';
    return {
      label: `${firstName} ${lastName}`.trim(),
      value: patient.ID!,
      searchString:
        `${firstName} ${lastName} ${patient.PreferredName ?? ''} ${firstName
          .toLowerCase()
          .substr(0, 3)}${lastName.toLowerCase().substr(0, 3)}`.toLowerCase(),
    };
  }

  openCreateGroupSession(): void {
    this.selectedGroupPatientIDs = [];
    this.createGroupSessionServiceID = null;
    this.refreshLinkableServices();
    this.showCreateGroupSession.set(true);
  }

  createGroupSession(): void {
    const currentPatientID = this.currentGroupPatientID;
    if (!currentPatientID) {
      this.alertService.error('Current client is missing');
      return;
    }
    if (this.selectedGroupPatientIDs.length < 1) {
      this.alertService.error('Select at least one additional client');
      return;
    }

    if (
      this.createGroupSessionServiceID &&
      !canLinkSessionToService(
        { StartedDate: null },
        this.services[this.createGroupSessionServiceID]
      )
    ) {
      this.alertService.error(SAME_DAY_SERVICE_LINK_ERROR);
      return;
    }

    const patientIDs = Array.from(
      new Set([currentPatientID, ...this.selectedGroupPatientIDs])
    );

    this.isCreatingGroupSession = true;
    this.groupSessionService
      .createGroupSession(
        patientIDs,
        this.academyModeService.getCurrentSparkModeId(),
        this.createGroupSessionServiceID
      )
      .pipe(takeUntilDestroyed(this.componentDestroyRef))
      .subscribe({
        next: (group) => {
          this.isCreatingGroupSession = false;
          this.showCreateGroupSession.set(false);
          this.createGroupSessionServiceID = null;
          this.router.navigate(['/staff/group', group.ID], {
            queryParams: this.academyModeService.getAcademyModeQueryParams(),
          });
        },
        error: (error) => {
          this.isCreatingGroupSession = false;
          this.alertService.error(
            error?.error?.Message ||
              error?.message ||
              'Error creating group session'
          );
        },
      });
  }

  openSession(session: PatientSessionModel): void {
    const commands = session.GroupSessionID
      ? ['/staff/group', session.GroupSessionID]
      : [
          '/staff/home',
          session.PatientID ?? this.selectedPatient?.ID,
          session.ID,
        ];
    this.router.navigate(commands, {
      queryParams: this.academyModeService.getAcademyModeQueryParams(),
    });
  }

  hasUnsyncedOfflineData(session: PatientSessionModel) {
    //get the offline data for the session
    const offlineData = this.offlineDataService.getOfflinePatientData(
      this.selectedPatient?.ID ?? 0
    );
    if (!offlineData) {
      return false;
    }
    const sessionOfflineData = offlineData.sessions.find(
      (x) => x.ID === session.ID && x.isDirty
    );
    const progressNoteOfflineData = (
      offlineData.progressNotes[session.ID ?? 0] as any
    )?.isDirty;
    if (!sessionOfflineData && !progressNoteOfflineData) {
      return false;
    }
    return true;
  }

  isSessionDeleted(session: PatientSessionModel) {
    return session.DeletedDate != null;
  }

  hasSupervisorPermission(): boolean {
    return (
      this.authService.userInfo?.Permissions?.includes(
        Permissions.SparkApp_Supervisor
      ) ?? false
    );
  }

  messageForSessionDeleted(session: PatientSessionModel) {
    return `This session has been deleted in ${new Date(
      session.DeletedDate ?? ''
    ).toLocaleDateString()}.`;
  }

  syncPatientData() {
    this.offlineDataService
      .syncOfflineDataWithGroupReconciliation()
      .subscribe({
      next: (response) => {
        console.log('Synced patient data:', response);
        this.loadData();
      },
      error: (error) => {
        const message = this.messageFromSyncError(error);
        console.error('Error syncing patient data:', error);
        this.alertService.error('Error syncing patient data - ' + message);
        this.sendLogsService.addError(
          'Failed to sync patient data (sessions): ' + message,
          error?.stack
        );
      },
    });
  }

  private messageFromSyncError(error: any): string {
    if (typeof error === 'string' && error.trim()) {
      return error.trim();
    }
    const message =
      error?.Message ||
      error?.message ||
      error?.error?.Message ||
      error?.error?.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
    return 'Unknown error';
  }

  viewProgressNotes(session: PatientSessionModel) {
    //Load the session data
    this.isLoadingSession[session.ID ?? 0] = true;
    this.staffApiService
      .GetPatientSessionWithData({
        sessionID: session.ID,
      })
      .subscribe((session) => {
        //replace session with the new session
        this.showProgressNotes.update((oldValue) => true);
        this.sessionToShowProgressNote.set(session ?? null);
        this.isLoadingSession[session.ID ?? 0] = false;
      });
  }

  closeProgressNote() {
    this.showProgressNotes.update((oldValue) => false);
    this.sessionToShowProgressNote.set(null);
  }

  previewSession() {
    this.router.navigate(['/staff/preview/' + this.selectedPatient?.ID], {
      queryParams: this.getAcademyModeQueryParams(),
    });
  }

  getAcademyModeQueryParams(): SparkModeQueryParams {
    return this.academyModeService.getAcademyModeQueryParams();
  }

  // Method to expand a row programmatically
  expandRow(session: PatientSessionModel) {
    if (this.table) {
      if (!this.table.isRowExpanded(session)) {
        this.table.toggleRow(session);
      }
    }
  }

  // Method to expand a row by session ID
  expandRowById(sessionId: number) {
    const session = this.sessions().find((s) => s.ID === sessionId);
    if (session && this.table) {
      // only toggle if collapsed.
      if (!this.table.isRowExpanded(session)) {
        this.table.toggleRow(session);
      }
    }
  }

  // Method to expand all rows
  expandAllRows() {
    if (this.table) {
      this.sessions().forEach((session) => {
        this.table.toggleRow(session);
      });
    }
  }

  // Method to collapse all rows
  collapseAllRows() {
    if (this.table) {
      this.sessions().forEach((session) => {
        if (this.table.isRowExpanded(session)) {
          this.table.toggleRow(session);
        }
      });
    }
  }

  // Client info handlers
  viewClientInfo(session: PatientSessionModel) {
    if (session.ServicePatientInfo) {
      this.clientInfoSession.set(session);
      this.showClientInfo.update(() => true);
      return;
    }

    this.alertService.error('Client Info not available for this session.');
  }

  closeClientInfo() {
    this.showClientInfo.update(() => false);
    this.clientInfoSession.set(null);
  }

  showReopenSessionAlert(session: PatientSessionModel) {
    const message =
      'Reopening this session will allow you to use the staff screen to take data again. However, data taken after reopening will not be processed for promotions and mastering logic - it will be just for recording purposes.';

    this.alertService
      .confirm(message, 'Are you sure you want to reopen this session?')
      .subscribe((response: boolean) => {
        if (response) {
          this.reopenSession(session);
        }
      });
  }

  reopenSession(session: PatientSessionModel) {
    console.log('Reopening session:', session);
    this.isLoading = true;

    // Set FinishedDate to null to reopen the session
    session.FinishedDate = null;

    this.staffApiService
      .SavePatientSession({
        body: session,
      })
      .subscribe(
        (response) => {
          console.log('Session reopened:', response);

          this.alertService.success('Session reopened successfully');

          this.isLoading = false;

          this.patientSessionService.reopenSession(session.ID ?? 0);
        },
        (error) => {
          console.error('Error reopening session:', error);
          this.alertService.error('Error reopening session - ' + error.Message);
          this.isLoading = false;
        }
      );
  }
}
